import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { FindLoad, FindSuccessItem, goalDataTypeEnums, Images, renderGoalTypes, useModal, renderIntervalText } from 'utils';
import { CreateChancel, DateCalendarForm, EditWithIcon, Input, NotError, TimeInputFull } from 'components';
import { baselineActions, httpRequestsOnSuccessActions } from 'store';
import { formatTime, latencyEnumBtn } from '../../../../../clinicalFragments/session/core/sessionCards/constants';
import { HourMinuteSecondInput } from './constants';
import { toast } from 'react-toastify';

export const AddBaseline = ({ currentItem, target, fromTrial, afterSuccess }) => {
    const ACTION_TYPE = fromTrial ? 'CREATE_BASELINE_SESSION' : currentItem ? 'EDIT_BASELINE_SESSION' : 'CREATE_BASELINE';
    const goalById = useSelector((state) => state.goals.goalById);
    const dispatch = useDispatch();
    const { close } = useModal();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const {
        handleSubmit,
        control,
        watch,
        reset,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: {
            goalId: goalById?.id,
            sessionDate: null,
            status: 'ACTIVE',
            targetId: target?.id,
            name: null,
            trials: [
                {
                    label: null,
                    value: null,
                },
            ],
        },
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'trials',
    });
    const trials = watch()?.trials || [];
    const lastTrial = trials[trials.length - 1];
    const checkRequiredFields =
        goalById?.dataType === goalDataTypeEnums.LATENCY
            ? lastTrial?.label && (lastTrial?.value || lastTrial?.value === 0)
            : goalById?.dataType === goalDataTypeEnums.RATE
              ? lastTrial?.startTime && lastTrial?.endTime && (lastTrial?.value || lastTrial?.value === 0)
              : lastTrial?.value || lastTrial?.value === 0;

    const timerValue =
        goalById?.dataType === goalDataTypeEnums.LATENCY ||
        goalById?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
        goalById?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
        goalById?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING
            ? goalById?.options[0]
            : null;
    const maxTime = timerValue?.value;
    const [removed, setRemoved] = useState([]);

    useEffect(() => {
        if (currentItem) {
            const params = {
                sessionDate: currentItem?.sessionDate ? currentItem?.sessionDate : null,
                trials: currentItem?.trials?.length
                    ? currentItem?.trials
                    : [
                          {
                              label: null,
                              value: null,
                          },
                      ],
            };
            reset(params);
        }
    }, [currentItem]);

    useEffect(() => {
        if (success) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            if (afterSuccess) {
                afterSuccess();
            }
        }
    }, [success]);

    const addNewTrial = () => {
        if (checkRequiredFields) {
            append({
                label: null,
                value: null,
            });
            clearErrors();
        } else {
            setError(`trials.${trials.length - 1}.label`, {
                type: 'manual',
                message: 'Last trial must have label and value (0 is allowed)',
            });
        }
    };
    const handleCreate = (data) => {
        if (checkRequiredFields) {
            clearErrors();

            if (goalById?.dataType === goalDataTypeEnums.RATE) {
                for (let i = 0; i < data.trials.length; i++) {
                    const { startTime, endTime } = data.trials[i];

                    if (startTime && endTime) {
                        const startMoment = moment(startTime);
                        const endMoment = moment(endTime);

                        if (!endMoment.isAfter(startMoment)) {
                            toast(<NotError type="error" errMessage={`Trial ${i + 1}: End time must be after start time.`} />);

                            setError(`trials.${trials.length - 1}.label`, {
                                type: 'manual',
                                message: `Trial ${i + 1}: End time must be after start time.`,
                            });
                            return;
                        }
                    }
                }
            }

            if (fromTrial) {
                dispatch(baselineActions.createBaselineSession(fromTrial?.id, data));
            } else if (currentItem) {
                const params = {
                    ...data,
                };
                params.trials = [...data?.trials, ...removed];
                dispatch(baselineActions.editBaselineSession(currentItem?.baselineId, currentItem?.id, params));
            } else {
                dispatch(baselineActions.createBaseline(data));
            }
        } else {
            setError(`trials.${trials.length - 1}.label`, {
                type: 'manual',
                message: 'Last trial must have label and value (0 is allowed)',
            });
        }
    };

    const changeTrialValue = (name, value, checkCase) => {
        setValue(name, value);

        if (!checkCase) {
            clearErrors();
        }
    };

    const updateDateTime = (oldDateTime, newTime) => {
        if (!newTime) return null;

        const base = oldDateTime ? moment.utc(oldDateTime) : moment.utc(); // fallback to today

        const [h, m, s] = newTime.split(':').map(Number);

        return base.set({ hour: h, minute: m, second: s, millisecond: 0 }).toISOString();
    };

    return (
        <div style={{ width: 700 }}>
            <div className="space-between" style={{ marginTop: 8 }}>
                <p className="modal-header-title">{currentItem ? 'Edit Baseline' : 'Add Baseline'}</p>
                <div className="correct-goal-box">{renderGoalTypes(goalById?.dataType)}</div>
            </div>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    {!currentItem && !fromTrial && (
                        <Input
                            type="text"
                            name="name"
                            label="Baseline Name*"
                            variant="outlined"
                            control={control}
                            rules={{
                                required: 'Baseline Name is required.',
                            }}
                        />
                    )}
                    <DateCalendarForm
                        name="sessionDate"
                        label="Session Date*"
                        control={control}
                        rules={{ required: 'Session date is required.' }}
                    />

                    <div>
                        {(goalById?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
                            goalById?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
                            goalById?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING) && (
                            <p className="interval-info-tex">{renderIntervalText(goalById?.dataType)}</p>
                        )}

                        {watch()?.trials?.map((item, index) => {
                            return (
                                <div
                                    key={index}
                                    className="baseline-trial-box"
                                    style={{ border: `1px solid ${errors?.trials?.[index]?.label ? '#FE7070' : '#EBF2FD'}` }}
                                >
                                    <div className="space-between-flex-start">
                                        <p className="baseline-trial-title">{`Trial ${index + 1}`}</p>
                                        {watch('trials')?.length > 1 && item?.id ? (
                                            <EditWithIcon
                                                styles={{ opacity: watch('trials')?.length === 1 ? 0.5 : 1 }}
                                                disabled={watch('trials')?.length === 1}
                                                icon={Images.redTrash}
                                                type={'button'}
                                                handleClick={() => {
                                                    const arr = [...removed];
                                                    if (item?.id) {
                                                        arr.push({
                                                            ...item,
                                                            deleted: true,
                                                        });
                                                        setRemoved(arr);
                                                    }
                                                    remove(index);
                                                }}
                                            />
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                    {goalById?.dataType === goalDataTypeEnums.PERCENT && (
                                        <div className="list-target-btn-wrapper">
                                            {goalById?.options?.map((i, j) => (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        changeTrialValue(`trials.${index}`, {
                                                            ...item,
                                                            label: i?.label,
                                                            value: i?.value,
                                                        });
                                                    }}
                                                    name={`trials.${index}.label`}
                                                    key={j}
                                                    className={
                                                        i?.value === 0
                                                            ? `target-status-no-response-btn-box ${
                                                                  i?.label === item?.label ? 'active' : ''
                                                              }`
                                                            : `target-status-btn-box ${i?.label === item?.label ? 'active' : ''}`
                                                    }
                                                >
                                                    {i?.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {(goalById?.dataType === goalDataTypeEnums.FREQUENCY ||
                                        goalById?.dataType === goalDataTypeEnums.SCORE) && (
                                        <input
                                            className="baseline-trial-input"
                                            name={`trials.${index}.value`}
                                            type="number"
                                            placeholder={'0'}
                                            value={item?.value || ''}
                                            onChange={(e) => {
                                                changeTrialValue(`trials.${index}`, {
                                                    ...item,
                                                    label: null,
                                                    value: +e?.target?.value,
                                                });
                                            }}
                                        />
                                    )}

                                    {goalById?.dataType === goalDataTypeEnums.RATING && (
                                        <div className="list-target-btn-wrapper">
                                            {goalById?.options?.map((i, j) => (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        changeTrialValue(`trials.${index}`, {
                                                            ...item,
                                                            label: i?.label,
                                                            value: i?.value,
                                                        });
                                                    }}
                                                    name={`trials.${index}.label`}
                                                    key={j}
                                                    className={`target-status-blue-btn-box ${i?.label === item?.label ? 'active' : ''}`}
                                                >
                                                    {i?.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {goalById?.dataType === goalDataTypeEnums.DURATION && (
                                        <HourMinuteSecondInput
                                            value={item?.value || 0}
                                            onChange={(totalSeconds) => {
                                                changeTrialValue(`trials.${index}`, {
                                                    ...item,
                                                    label: null,
                                                    value: totalSeconds ? totalSeconds : null,
                                                });
                                            }}
                                        />
                                    )}

                                    {goalById?.dataType === goalDataTypeEnums.LATENCY && (
                                        <div className="flex-align-center" style={{ gap: 16 }}>
                                            <HourMinuteSecondInput
                                                value={item?.value || 0}
                                                onChange={(totalSeconds) => {
                                                    changeTrialValue(`trials.${index}`, {
                                                        ...item,
                                                        label: item?.label ? item?.label : null,
                                                        value: totalSeconds ? totalSeconds : null,
                                                    });
                                                }}
                                            />
                                            <div className="flex-align-center" style={{ gap: 8 }}>
                                                {latencyEnumBtn.map((i, j) => (
                                                    <button
                                                        type="button"
                                                        disabled={!item?.value}
                                                        key={j}
                                                        className={`${i.class} ${item?.label === i.value ? 'active' : ''}`}
                                                        style={{ width: 145, opacity: item?.value ? 1 : 0.5 }}
                                                        onClick={() => {
                                                            changeTrialValue(
                                                                `trials.${index}`,
                                                                {
                                                                    ...item,
                                                                    label: i.value,
                                                                    value: item?.value,
                                                                },
                                                                true
                                                            );
                                                            if (item?.value) {
                                                                clearErrors();
                                                            }
                                                        }}
                                                    >
                                                        <img src={i.icon} alt="" />
                                                        {i.title}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(goalById?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
                                        goalById?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
                                        goalById?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING) && (
                                        <div className="flex-align-center" style={{ gap: 16 }}>
                                            <div className="timer-box">{formatTime(maxTime)}</div>
                                            <div className="flex-align-center" style={{ gap: 8 }}>
                                                <button
                                                    onClick={() => {
                                                        changeTrialValue(`trials.${index}`, {
                                                            ...item,
                                                            label: null,
                                                            value: 1,
                                                        });
                                                    }}
                                                    type={'button'}
                                                    style={{ width: 76 }}
                                                    className={`target-status-blue-btn-box ${item?.value === 1 ? 'active' : ''}`}
                                                >
                                                    <img src={Images.checkmarkWhite} alt="mark" />
                                                    Yes
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        changeTrialValue(`trials.${index}`, {
                                                            ...item,
                                                            label: null,
                                                            value: 0,
                                                        });
                                                    }}
                                                    type={'button'}
                                                    style={{ width: 76 }}
                                                    className={`target-status-no-response-btn-box ${item?.value === 0 ? 'active' : ''}`}
                                                >
                                                    <img src={Images.remove} alt="mark" />
                                                    No
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {goalById?.dataType === goalDataTypeEnums.RATE && (
                                        <div className="baseline-rate-wrapper">
                                            <div className="flex-align-center" style={{ gap: 16 }}>
                                                <TimeInputFull
                                                    label="Start Time"
                                                    customClass="history-log-time-input"
                                                    name="startTime"
                                                    onChange={(e) => {
                                                        const timeValue = e.target.value;
                                                        const updatedDateTime = updateDateTime(item?.startTime, timeValue);
                                                        changeTrialValue(`trials.${index}.startTime`, updatedDateTime);
                                                    }}
                                                    errorFalse={true}
                                                    defaultValue={item?.startTime ? item?.startTime?.slice(11, 19) : null}
                                                />
                                                <TimeInputFull
                                                    label={'End Time'}
                                                    customClass={'history-log-time-input'}
                                                    name={'endTime'}
                                                    onChange={(e) => {
                                                        clearErrors();
                                                        const timeValue = e.target.value;
                                                        const updatedDateTime = updateDateTime(item?.endTime, timeValue);
                                                        changeTrialValue(`trials.${index}.endTime`, updatedDateTime);
                                                    }}
                                                    errorFalse={true}
                                                    defaultValue={item?.endTime ? item?.endTime?.slice(11, 19) : null}
                                                />
                                                <input
                                                    className="baseline-trial-input"
                                                    name={`trials.${index}.value`}
                                                    type="number"
                                                    placeholder={''}
                                                    value={item?.value === 0 ? 0 : item?.value}
                                                    onChange={(e) => {
                                                        const value = e?.target?.value;
                                                        changeTrialValue(`trials.${index}.value`, value ? +value : null);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <button type="button" onClick={addNewTrial} className="add-trial-btn">
                            <img src={Images.addHours} alt="add" />
                            <p>{goalById?.dataType === goalDataTypeEnums.DURATION ? 'Add Duration' : 'Add Trial'}</p>
                        </button>
                    </div>
                </div>

                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={currentItem ? 'Save' : 'Add'}
                    chancel="Cancel"
                    onClose={close}
                    buttonWidth="100%"
                />
            </form>
        </div>
    );
};
